import { ReactElement, useEffect } from 'react';
import ModalImg from '../../assets/images/section-modal.png';
import {
  StatusBar,
  Input,
  Modal,
  PrimaryButton,
  SecondaryButton,
  Spinner,
  Typography,
  SnackbarStatus,
} from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { useMenu } from 'src/hooks/useMenu/useMenu';
import { ISection } from '../../types/menu.types';
import { createNewMenuSection } from 'src/api/createNewMenuSection/createNewMenuSection';
import { updateMenuSection } from 'src/api/updateMenuSection/updateMenuSection';
import styles from './SectionModal.module.scss';
import { ModalProps } from './types';
import { CheckboxList } from '../CheckboxList/CheckboxList';
import { useMealtimes } from '../../hooks/useMealtimes/useMealtimes';
import { IMealtime } from '../../types/mealtimes.types';
import { updateMealtimes } from './updateMealtimes/updateMealtimes';
import { QueryKeys } from 'src/hooks/QueryKeys';

export interface ISectionFormTypes {
  name: string;
  description: string;
  items: Record<string, boolean>;
  parent_category_id: string | null;
  mealtimes: Record<string, boolean>;
}

const SectionModalContent = ({ closeModal, menuId, parentSectionId = null, section }: ModalProps): ReactElement => {
  const queryClient = useQueryClient();
  const { data: menu } = useMenu(menuId);
  const { errorMessage, showNotification, clearError } = useModalNotification();

  const { data: mealtimes, isLoading: isMealtimesLoading } = useMealtimes(menuId);

  const getInitialItems = (section: ISection): Record<string, boolean> => {
    const initialItems = {};
    section.items.forEach((item) => {
      initialItems[item.itemId] = true;
    });
    return initialItems;
  };

  const getInitialMealtimes = (sectionId: string, mealtimes: IMealtime[]) => {
    const initialMealtimes = {};
    mealtimes.forEach((mealtime) => {
      const isSectionInMealtime = !!mealtime.categories.find((m) => m.category_id === sectionId);
      initialMealtimes[mealtime.mealtime_id] = isSectionInMealtime;
    });
    return initialMealtimes;
  };

  const {
    handleSubmit,
    formState: { isSubmitting, errors },
    register,
    setValue,
    watch,
  } = useForm<ISectionFormTypes>({
    defaultValues: {
      name: section?.name || '',
      description: section?.description || '',
      items: section ? getInitialItems(section) : {},
      parent_category_id: parentSectionId,
      mealtimes: {},
    },
  });

  useEffect(() => {
    if (mealtimes && section) {
      setValue('mealtimes', getInitialMealtimes(section.sectionId, mealtimes));
    }
  }, [mealtimes, section]);

  const parentSection = menu?.sections.find((section) => section.sectionId === parentSectionId);

  const addableItems = [
    ...(section ? section.items : []),
    ...(menu.uncategorised || []),
    ...(parentSection?.items || []),
  ];

  const onSubmit = async (data: ISectionFormTypes) => {
    clearError();
    const items = Object.keys(data.items).filter((item) => data.items[item]);
    const sectionRequest = () => {
      if (section) {
        return updateMenuSection(menuId, section.sectionId, {
          name: data.name,
          description: data.description,
          items,
        });
      }
      return createNewMenuSection(menuId, {
        name: data.name,
        description: data.description,
        items,
        parent_section_id: data.parent_category_id,
      });
    };
    try {
      const sectionRes = await sectionRequest();
      const sectionJson = await sectionRes.json();
      const sectionId = sectionJson.data[0].section_id;

      await updateMealtimes(menuId, sectionId, data.mealtimes, mealtimes);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });

      closeModal();
    } catch (e) {
      showNotification({
        status: SnackbarStatus.ERROR,
        message: `Something went wrong whilst ${section ? 'updating' : 'creating'} the menu category`,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Typography size="xxlarge" weight="semi-bold" className={styles['title']}>
        {`${section ? 'Edit' : 'Create'} menu ${parentSectionId ? 'subcategory' : 'category'}`}
      </Typography>
      <Input
        type="text"
        label="Name"
        hasMargin
        register={register('name', { required: 'Please enter a menu name' })}
        error={errors?.name?.message}
      />

      <Input type="text" label="Description" rows={2} hasMargin register={register('description')} />

      {parentSectionId && (
        <div className={styles['parent-category']}>
          <Typography weight="semi-bold">Main category:</Typography>
          <Typography>{parentSection.name}</Typography>
        </div>
      )}

      {mealtimes?.length > 0 && !parentSectionId && (
        <>
          <div className={styles['checkbox-container']}>
            <Typography weight="semi-bold">Mealtimes</Typography>
            {isMealtimesLoading ? (
              <div className={styles['spinner-container']}>
                <Spinner size="medium" />
              </div>
            ) : (
              <CheckboxList
                register={register}
                watch={watch}
                storagePrefix="mealtimes"
                setValue={setValue}
                checkboxes={mealtimes.map((mealtime) => ({ id: mealtime.mealtime_id, label: mealtime.name }))}
              />
            )}
          </div>
        </>
      )}

      {addableItems?.length > 0 && (
        <>
          <div className={styles['checkbox-container']}>
            <Typography weight="semi-bold">Select products to include</Typography>
            <CheckboxList
              register={register}
              watch={watch}
              storagePrefix="items"
              setValue={setValue}
              checkboxes={addableItems.map((item) => ({ id: item.itemId, label: item.name }))}
            />
          </div>
        </>
      )}

      <div className={styles['button-container']}>
        <SecondaryButton size="full-width" type="button" buttonStyle="outline" onClick={closeModal}>
          Close
        </SecondaryButton>
        <PrimaryButton size="full-width" type="submit" loading={isSubmitting} disabled={isSubmitting}>
          {section ? 'Save' : 'Create'}
        </PrimaryButton>
      </div>
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </form>
  );
};

const SectionModal = ({ closeModal, open, menuId, parentSectionId = null, section }: ModalProps): ReactElement => {
  return (
    <Modal
      closeModal={closeModal}
      open={open}
      image={ModalImg}
      imageAlt={'Picture of four beautifully displayed breakfast plates'}
    >
      <SectionModalContent
        closeModal={closeModal}
        open={open}
        menuId={menuId}
        parentSectionId={parentSectionId}
        section={section}
      />
    </Modal>
  );
};

export default SectionModal;
